import { createFileRoute } from "@tanstack/react-router";
import { AccessDenied } from "@/components/nancy/access-denied";
import { guardFeature } from "@/lib/policy/route-guard";
import { PageHeader } from "@/components/app/shell/page-header";
import { settingsSearch } from "@/routes/_authenticated/nancy/-lib/search-params";
import { useNancyFacility } from "@/routes/_authenticated/nancy/-lib/use-nancy-facility";
import { SettingsForm } from "@/routes/_authenticated/nancy/-components/settings/settings-form";
import { KnowledgeSettings } from "@/routes/_authenticated/nancy/-components/memory/knowledge-settings";

// No loader: the building is the header switcher's choice, known only once facilities load.
export const Route = createFileRoute("/_authenticated/ws/nancy/_gate/settings")({
  validateSearch: settingsSearch,
  // The feature gate only. nancy:configure is checked in the screen, FACILITY-scoped: a facility
  // admin who is staff in the org holds it only through the building, which `guardRoute` can't see.
  beforeLoad: ({ context }) => guardFeature(context.me, "nancy-settings"),
  head: () => ({
    meta: [
      { title: "Nancy settings | People AI" },
      {
        name: "description",
        content:
          "Where Nancy shops and delivers, what she knows, and the computer she shops through.",
      },
    ],
  }),
  component: NancySettings,
});

function NancySettings() {
  const { facilityId, can } = useNancyFacility();

  if (!can("read")) {
    return (
      <AccessDenied
        title="No access to Nancy"
        description="You do not have access to Nancy's settings in this community."
      />
    );
  }

  // Someone who works with Nancy but cannot configure her still reads what she knows here.
  const configures = can("configure");
  return (
    <>
      <PageHeader title={configures ? "Settings" : "What Nancy knows"} />
      {configures ? (
        // One form per building: it keeps unsaved edits through a refetch, so without the key an
        // address typed for one building stayed on screen for the next and saved into it.
        <SettingsForm key={facilityId} facilityId={facilityId} />
      ) : (
        <KnowledgeSettings facilityId={facilityId} />
      )}
    </>
  );
}
